import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

import { ProductV2, ProductV2Variation } from '../models/product-v2.model';
import { AuthService } from '../services/auth.service';
import { CartFacade } from '../store/cart/cart.facade';
import { UserFacade } from '../store/user/user.facade';
import { STORAGE_REDIRECT_AFTER_LOGIN } from '../utils/external-channel-access.util';

import { writeCampaignLoginFlowTypes, writeCampaignPurchaseIntent } from './campaign-checkout-flag';
import { CampaignFlowType } from './campaign-contract';
import { campaignLoginType } from './campaign-login-type';

@Injectable({ providedIn: 'root' })
export class CampaignPurchaseService {
  private readonly cart = inject(CartFacade);
  private readonly auth = inject(AuthService);
  private readonly userFacade = inject(UserFacade);
  private readonly router = inject(Router);

  buy(
    product: ProductV2,
    variation: ProductV2Variation,
    quantity: number,
    flowTypes: CampaignFlowType[]
  ): void {
    const allowed = new Set((product.variations ?? []).map(item => item.uuid));
    if (!allowed.has(variation.uuid)) {
      return;
    }

    writeCampaignLoginFlowTypes(flowTypes);
    writeCampaignPurchaseIntent({
      productUuid: product.uuid,
      variationUuid: variation.uuid,
      quantity,
    });

    const sessionType = campaignLoginType({
      authenticated: this.auth.isAuthenticated(),
      fixaSession: this.auth.isFixaSession(),
      resources: this.auth.getLoginData()?.resources,
    });

    let added = false;
    const subscription = this.cart.getCartItems().subscribe(items => {
      if (added) {
        return;
      }
      if ((items ?? []).length > 0) {
        this.cart.clearCart();
        return;
      }

      added = true;
      queueMicrotask(() => subscription.unsubscribe());
      this.cart.addToCart(product, quantity, variation);
      this.afterAdd(sessionType, flowTypes);
    });
  }

  private openLogin(): void {
    try {
      sessionStorage.setItem(STORAGE_REDIRECT_AFTER_LOGIN, '/checkout');
    } catch {
      // ignore
    }
    this.router.navigate(['/login']);
  }

  private afterAdd(sessionType: CampaignFlowType | null, flowTypes: CampaignFlowType[]): void {
    if (sessionType && flowTypes.includes(sessionType)) {
      this.router.navigate(['/checkout']);
      return;
    }

    if (sessionType) {
      this.auth.logout();
      this.userFacade
        .getIsAuthenticated()
        .pipe(
          filter(authenticated => !authenticated),
          take(1)
        )
        .subscribe(() => {
          this.openLogin();
        });
      return;
    }

    this.openLogin();
  }
}
